import { useEffect, useRef, useCallback } from "react";
import type { Router } from "expo-router";

import { useQueryClient } from "@tanstack/react-query";

import type { DeviceTokenApi } from "./device-token-api";
import {
  onTokenRefresh,
  onForegroundMessage,
  getInitialNotification,
  onNotificationOpenedApp,
  getDevicePlatform,
} from "./messaging-service";
import type { RemoteMessage } from "./messaging-service";
import { navigateToNotification } from "./navigate-to-notification";
import { useDeviceToken, STATUS_QUERY_KEY } from "./use-device-token";
import { isDeviceTokenTtlRefreshDue } from "./token-ttl";

export interface UsePushNotificationsOptions {
  /**
   * `isUnlocked` is whether the user has passed the app lock (PIN/biometrics)
   * this session. Notification taps wait for it so the destination never
   * renders behind — or instead of — the lock screen.
   */
  auth: { isAuthenticated: boolean; isInitialized: boolean; isUnlocked: boolean };
  router: Router;
  showForegroundToast: (title: string, body?: string) => void;
  deviceTokenApi: DeviceTokenApi;
  /**
   * Optional policy hook for deciding whether the current token should be
   * auto-registered when backend state reports unregistered.
   */
  shouldAutoRegisterToken?: (token: string) => boolean;
  /**
   * When false, skips token/device registration and notification listeners (e.g. product feature flag).
   * @default true
   */
  enabled?: boolean;
}

/**
 * Manages the full push notification lifecycle: permission, token registration,
 * foreground display, and notification-tap deep linking.
 *
 * All app-specific dependencies (auth state, router, toast, API client) are
 * injected via the options object so this hook can live in the feature package.
 *
 * Async state (permission check, token lookup, backend registration check) is
 * handled by React Query via `useDeviceToken`. Subscriptions are pure
 * synchronous effects gated on the query result.
 */
export function usePushNotifications({
  auth,
  router,
  showForegroundToast,
  deviceTokenApi,
  shouldAutoRegisterToken,
  enabled: featureEnabled = true,
}: UsePushNotificationsOptions) {
  const { isAuthenticated, isInitialized, isUnlocked } = auth;
  const queryClient = useQueryClient();
  const pendingDeepLinkRef = useRef<RemoteMessage | null>(null);
  const initialNotificationHandledRef = useRef(false);
  const handleTapRef = useRef<(message: RemoteMessage) => void>(undefined);
  const attemptedAutoRegistrationTokenRef = useRef<string | undefined>(undefined);
  const attemptedTtlRefreshKeyRef = useRef<string | undefined>(undefined);

  const { isRegistered } = useDeviceToken({
    deviceTokenApi,
    enabled: featureEnabled && isAuthenticated && isInitialized,
  });
  const status = isRegistered.data;
  const registered = status?.registered ?? false;
  const isAutoRegistrationAllowed = useCallback(
    (token: string): boolean => (shouldAutoRegisterToken ? shouldAutoRegisterToken(token) : true),
    [shouldAutoRegisterToken],
  );

  const registerToken = useCallback(
    async (fcmToken: string): Promise<boolean> => {
      if (!isAutoRegistrationAllowed(fcmToken)) return false;
      try {
        const platform = getDevicePlatform();
        await deviceTokenApi.registerToken(fcmToken, platform);
        // If user opted out while the PUT was in flight, immediately undo it.
        if (!isAutoRegistrationAllowed(fcmToken)) {
          try {
            await deviceTokenApi.unregisterToken(fcmToken);
          } catch (error) {
            console.error("[Push] Failed to rollback token registration after opt-out:", error);
          }
          return false;
        }
        void queryClient.invalidateQueries({ queryKey: STATUS_QUERY_KEY });
        return true;
      } catch (error) {
        console.error("[Push] Failed to register token:", error);
        return false;
      }
    },
    [deviceTokenApi, isAutoRegistrationAllowed, queryClient],
  );

  const navigate = useCallback((message: RemoteMessage) => navigateToNotification(router, message), [router]);

  // Keep the tap handler ref current so the mount-once effect below always
  // calls through to the latest closure without re-subscribing.
  handleTapRef.current = (message: RemoteMessage) => {
    if (!isAuthenticated || !isInitialized || !isUnlocked) {
      pendingDeepLinkRef.current = message;
      return;
    }
    navigate(message);
  };

  // Notification tap handling — subscribes once on mount.
  // Uses handleTapRef so the listener always sees current auth state.
  useEffect(() => {
    if (!featureEnabled) return;

    if (!initialNotificationHandledRef.current) {
      initialNotificationHandledRef.current = true;
      void getInitialNotification().then((message) => {
        if (message) handleTapRef.current?.(message);
      });
    }

    const unsubscribe = onNotificationOpenedApp((message) => {
      handleTapRef.current?.(message);
    });

    return unsubscribe;
  }, [featureEnabled]);

  // Drain any deep link that arrived before the app was unlocked. The unlock
  // screens replace themselves with the default landing screen in the same
  // tick they unlock, so this push lands on top of it and back returns there.
  useEffect(() => {
    if (!featureEnabled) return;
    if (!isAuthenticated || !isInitialized || !isUnlocked) return;

    const pending = pendingDeepLinkRef.current;
    if (pending) {
      pendingDeepLinkRef.current = null;
      navigate(pending);
    }
  }, [featureEnabled, isAuthenticated, isInitialized, isUnlocked, navigate]);

  // Keep backend registration aligned when DynamoDB TTL or transient backend state drops
  // an otherwise valid local FCM token.
  useEffect(() => {
    if (!featureEnabled || !isAuthenticated || !isInitialized) return;

    const token = status?.token;
    if (registered || status?.permission !== "granted" || !token) {
      if (registered) {
        attemptedAutoRegistrationTokenRef.current = undefined;
      }
      return;
    }

    if (shouldAutoRegisterToken && !shouldAutoRegisterToken(token)) return;
    if (attemptedAutoRegistrationTokenRef.current === token) return;
    attemptedAutoRegistrationTokenRef.current = token;
    void (async () => {
      const ok = await registerToken(token);
      if (!ok && attemptedAutoRegistrationTokenRef.current === token) {
        attemptedAutoRegistrationTokenRef.current = undefined;
      }
    })();
  }, [
    featureEnabled,
    isAuthenticated,
    isInitialized,
    status?.permission,
    status?.token,
    registered,
    registerToken,
    shouldAutoRegisterToken,
  ]);

  // Refresh backend registration before hard TTL expiry to avoid notification gaps
  // for inactive users who do not open the app exactly around expiry.
  useEffect(() => {
    if (!featureEnabled || !isAuthenticated || !isInitialized) return;
    if (!registered || status?.permission !== "granted") return;
    const token = status.token;
    const ttl = status.ttl;
    if (!token || typeof ttl !== "number") return;
    // Refresh should follow backend truth (currently registered), even when
    // legacy installs are missing the local opt-in storage key.
    if (!isDeviceTokenTtlRefreshDue(ttl)) return;

    const refreshKey = `${token}:${ttl.toString()}`;
    if (attemptedTtlRefreshKeyRef.current === refreshKey) return;
    attemptedTtlRefreshKeyRef.current = refreshKey;
    void (async () => {
      const ok = await registerToken(token);
      if (!ok && attemptedTtlRefreshKeyRef.current === refreshKey) {
        attemptedTtlRefreshKeyRef.current = undefined;
      }
    })();
  }, [
    featureEnabled,
    isAuthenticated,
    isInitialized,
    registered,
    status?.permission,
    status?.token,
    status?.ttl,
    shouldAutoRegisterToken,
    registerToken,
  ]);

  // Token refresh and foreground message listeners — only active while the
  // device token is registered on the backend. When the settings screen
  // toggles registration, React Query invalidates the status query, flipping
  // `registered` and causing this effect to subscribe/unsubscribe cleanly.
  useEffect(() => {
    if (!featureEnabled || !registered) return;

    const unsubRefresh = onTokenRefresh((newToken) => {
      void registerToken(newToken);
    });

    const unsubForeground = onForegroundMessage((message) => {
      const title = message.notification?.title ?? "Notification";
      const body = message.notification?.body;
      showForegroundToast(title, body);
    });

    return () => {
      unsubRefresh();
      unsubForeground();
    };
  }, [featureEnabled, registered, registerToken, showForegroundToast]);
}
